import { css } from "@emotion/css";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";

import { CheckboxRAC } from "@certn/cascadia-react";

import { useContract } from "./hooks";
import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";
import { Link } from "../../components/link/Link";
import { useMediaQuery } from "../../components/utils";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";
import { useScrollbarStyles } from "../../hooks/useScrollbarStyles";

type Props = {
  label?: string;
  accessID: string;
  index?: number; // number that points to the current displayed contract. Must be within "contractIDs" range
  contractIDs: string[];
  signerID: string;
  testMode?: boolean;
  errorMessage?: string;
  renderData?: Record<string, unknown>;
  isSigned?: boolean;
  onChange?: (isSelected: boolean) => void;
  onContractLoad?: (contract: { isSigned: boolean; versionID: string }) => void;
  skipHasToScroll?: boolean;
};

export function ContractScroll({
  index = 0,
  label,
  accessID,
  contractIDs,
  signerID,
  renderData,
  isSigned,
  onChange,
  onContractLoad,
  errorMessage,
  skipHasToScroll = false,
  testMode = true,
}: PropsWithChildren<Props>) {
  const styles = useStyles();
  const {
    data: contractsData,
    isAgreed,
    getContractHTML,
    isError,
  } = useContract({
    accessID,
    contractIDs,
    signerID,
    renderData,
    testMode,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [hasToScroll, setHasToScroll] = useState<boolean>(false);
  const currentContractID = contractIDs[index];
  const currentContractAgreed = isAgreed(currentContractID);

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    if (!contractsData) {
      return;
    }

    if (skipHasToScroll) {
      return;
    }
    setHasToScroll(
      !isSigned &&
        !currentContractAgreed &&
        contentRef.current.scrollHeight > contentRef.current.clientHeight
    );
  }, [
    contractsData,
    contentRef,
    currentContractAgreed,
    index,
    isSigned,
    skipHasToScroll,
  ]);

  useEffect(() => {
    // scroll to top, when new contract is displayed
    contentRef.current?.scrollTo(0, 0);
  }, [contentRef, index]);

  useEffect(() => {
    // initial agreement state of the contract
    onContractLoad?.({
      isSigned: currentContractAgreed,
      versionID:
        contractsData?.contract_data[currentContractID]?.published_version ??
        "",
    });
  }, [
    currentContractAgreed,
    index,
    contractsData,
    onContractLoad,
    currentContractID,
  ]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.target as HTMLDivElement;

    if (element.scrollHeight <= element.scrollTop + element.clientHeight + 50) {
      setHasToScroll(false);
    }
  };

  return (
    <div>
      <section
        className={css(styles.section, {
          borderColor: errorMessage ? "var(--border-danger-input)" : undefined,
        })}
      >
        <header className={styles.header}>
          <Link
            testID="contract-download-link"
            href={contractsData?.contract_data[currentContractID]?.download_url}
            target="_blank"
            textColor="text-weak"
            textStyle="global/body-secondary"
          >
            <Trans>Download</Trans>
          </Link>
        </header>

        <div
          ref={contentRef}
          className={styles.content}
          dangerouslySetInnerHTML={
            isError ? undefined : { __html: getContractHTML(currentContractID) }
          }
          aria-label="contract-content"
          role="textbox"
          tabIndex={0}
          onScroll={handleScroll}
        >
          {isError && (
            <Text color="text-weak" testID="contract-fetch-error">
              <Trans>Something went wrong</Trans>
            </Text>
          )}
        </div>

        <footer
          className={css(styles.footer, {
            textAlign: hasToScroll ? "center" : "left",
          })}
        >
          {contractsData &&
            (hasToScroll ? (
              <Text testID="please-scroll-text">
                <Trans>Please scroll to the bottom to accept</Trans>
              </Text>
            ) : (
              <CheckboxRAC
                isSelected={currentContractAgreed || isSigned}
                onChange={onChange}
                isDisabled={currentContractAgreed}
                testID="contract-scroll"
              >
                {label || (
                  <Trans>I understand and agree to this document</Trans>
                )}
              </CheckboxRAC>
            ))}
        </footer>
      </section>
      {errorMessage && (
        <FormFeedback type="invalid">{errorMessage}</FormFeedback>
      )}
    </div>
  );
}

function useStyles() {
  const { mediaQuery } = useMediaQuery();
  const colors = useColors();
  const scrollbarStyles = useScrollbarStyles();

  return {
    section: css(
      {
        backgroundColor: "var(--background-base)",
        width: "100%",
        height: "320px",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${colors["border-base"]}`,
        borderRadius: "var(--border-radius-3)",
      },
      mediaQuery({
        maxWidth: ["unset", "580px"],
      })
    ),
    header: css(
      {
        textAlign: "right",
      },
      mediaQuery({
        padding: [
          `${"var(--space-3)"} ${"var(--space-4)"}`,
          `${"var(--space-3)"} ${"var(--space-5)"}`,
        ],
      })
    ),
    footer: css(
      mediaQuery({
        padding: [
          `${"var(--space-2)"} ${"var(--space-4)"}`,
          `${"var(--space-2)"} ${"var(--space-5)"}`,
        ],
      })
    ),
    content: css(
      {
        flex: 1,
        borderTop: `1px solid ${colors["border-base"]}`,
        borderBottom: `1px solid ${colors["border-base"]}`,
        overflow: "auto",
        // text color for Clickwrap document
        "& *": {
          color: `${colors["text-weak"]} !important`,
        },
        // override Clickwrap styles
        "& .ps-contract-scroll": {
          maxHeight: "unset !important",
          padding: "0 !important",
          border: "none !important",
        },
      },
      scrollbarStyles,
      mediaQuery({
        padding: ["var(--space-4)", `${"var(--space-4)"} ${"var(--space-5)"}`],
      })
    ),
  };
}
